import type { Account } from '@livecontrol/scheduler/model';
import _ from 'lodash';
import { validateEventConflicts } from './conflict-validator';
import { validateCreditAvailability } from './credit-validator';
import { validateAudioCuesheetURL, validateCuesheetURL } from './cuesheets-validator';
import { validateEventDescription } from './description-validator';
import { validateEventBackground } from './event-background-validator';
import { validateEventLeadtimeAcknowledge } from './leadtime-acknwoledge-validator';
import { validateEventLocation } from './location-validator';
import { validateMaxRecurrences } from './max-recurrence-validator';
import { validateMobileEventConflicts } from './mobile-conflict-validator';
import { validateMobileEventDescription } from './mobile-description-validator';
import { validateMobileEventLocation } from './mobile-location-validator';
import { validateMobileEventTitle } from './mobile-title-validator';
import { validateEventNotes } from './notes-validator';
import { validateEventPassword } from './password-validator';
import { getAllRecurrences } from './recurrence-calculator';
import { validateEventRedirect } from './redirect-validator';
import { validateDefaultSlides, validateSlides } from './slides-validator';
import { validateEventTitle } from './title-validator';
import { validateEventType } from './type-validator';
import { EventValidator } from './types';
import { validateZoomDetails } from './zoom-validator';

export const validateEventForm = (
  {
    availableCredits,
    timezone,
    events,
    newEventInfo,
    simulatedLiveAssetIsUpload,
    cueSheetURL,
    audioCueSheetURL,
    isAdmin,
    isProd = true,
    hasDefaultSlides = false
  }: EventValidator.NewEventProps,
  billing?: Account.Billing
): EventValidator.Errors => {

  const isProducedAndMobileFlow = newEventInfo.type === EventValidator.ProductionType.PRODUCED_AND_MOBILE_KIT ||
      newEventInfo.type === EventValidator.ProductionType.PRODUCED_AND_MOBILE_APP;

  const errors: EventValidator.Errors = {
    // Del primer validador (details)
    ...validateEventType(newEventInfo),
    ...validateEventTitle(newEventInfo),
    ...validateEventLocation(newEventInfo),
    ...validateEventDescription(newEventInfo),
    ...validateEventNotes(newEventInfo),
    ...validateEventLeadtimeAcknowledge(newEventInfo),
    // Del segundo validador (step4)
    ...validateEventRedirect(newEventInfo),
    ...validateDefaultSlides(newEventInfo, hasDefaultSlides),
    // Compartidos por ambos
    ...validateEventBackground(newEventInfo),
    ...validateSlides(newEventInfo),
    ...validateZoomDetails(newEventInfo),
    ...validateEventPassword(newEventInfo),
    ...(cueSheetURL && validateCuesheetURL(cueSheetURL)),
    ...(audioCueSheetURL && validateAudioCuesheetURL(audioCueSheetURL))
  };

  if (isProducedAndMobileFlow) {
    _.merge(errors, validateMobileEventTitle(newEventInfo));
    _.merge(errors, validateMobileEventLocation(newEventInfo));
    _.merge(errors, validateMobileEventDescription(newEventInfo));
  }

  const { recurrences, errors: repeatingEventErrors } = getAllRecurrences({
    newEventInfo,
    simulatedLiveAssetIsUpload,
    isAdmin,
    isProd,
    timezone: timezone.name
  });

  if (repeatingEventErrors) {
    _.merge(errors, repeatingEventErrors);
  } else if (recurrences?.length) {
    _.merge(errors, validateMaxRecurrences(newEventInfo, recurrences));
    _.merge(errors, validateCreditAvailability(availableCredits, newEventInfo, recurrences, billing));
  }

  if (_.isEmpty(errors.date)) {
    _.merge(
      errors,
      validateEventConflicts({
        newEventInfo,
        simulatedLiveAssetIsUpload,
        events,
        timezone,
        recurrences,
        isAdmin,
        isProd
      })
    );
  }

  if (_.isEmpty(errors.mobileDate) && isProducedAndMobileFlow) {
    _.merge(
      errors,
      validateMobileEventConflicts({
        newEventInfo,
        simulatedLiveAssetIsUpload,
        events,
        timezone,
        isAdmin,
        isProd,
        isProducedAndMobileFlow
      })
    );
  }

  return errors;
};
